import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaGlobe, FaArrowLeft, FaTimes } from "react-icons/fa";
import { useLanguage } from "../../components/LanguageContext";
import { ClickToPath, Language, CreateStepData } from "../../Types/types";
import { addpackagetranslation } from "./addpackagetranslation";
import { AddLanguageModal } from "../../components/shared/AddLanguageModal";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import RotationInput from "./RotationInput";
import PanoramaInputs from "./PanoramaInputs";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  RootState,
} from "../../components/Manual_List/fetch/store";
import {
  fetchContentTypes,
  fetchFullPackageById,
  createStep,
  fetchLinksForPanoramaCreate,
  postLink,
  uploadContentThumbnail,
  uploadContent,
} from "../../components/Manual_List/fetch/slices";
import { secondaryFiletrnslts } from "./secondaryFiletrnslts";

const CreateStep: React.FC = () => {
  const { language } = useLanguage();
  const t = addpackagetranslation[language as Language];
  const t2 = secondaryFiletrnslts[language as Language];
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [stepNumbers, setStepNumbers] = useState<number[]>([]);
  const state = location.state || {};
  const contentTypes = useSelector(
    (state: RootState) => state.data.contentTypes.data
  );
  const { id } = useParams<{ id: string }>();
  const [stepNames, setStepNames] = useState<Record<string, string>>({});
  const [stepType, setStepType] = useState<string>("");
  const [stepFile, setStepFile] = useState<File | null>(null);
  const [secondaryFile, setSecondaryFile] = useState<File | null>(null);
  const [stepRotation, setStepRotation] = useState<number>(0);
  const [stepClickToPaths, setStepClickToPaths] = useState<ClickToPath[]>([]);
  const [isStepNameModalOpen, setIsStepNameModalOpen] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  const steps = useSelector(
    (state: RootState) => state.data.fullPackage.data?.packageContents
  );
  const links = useSelector((state: RootState) => state.data.link.data);

  useEffect(() => {
    dispatch(fetchContentTypes(language));
    if (id) {
      dispatch(fetchFullPackageById({ id, language }));
      dispatch(fetchLinksForPanoramaCreate({ id }));
    }
  }, [dispatch, language, id]);

  useEffect(() => {
    if (location.state && location.state.steps) {
      setStepNumbers(location.state.steps);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const selectedContentType = contentTypes.find(
      (type) => type.name === stepType
    );
    if (!selectedContentType) {
      console.error("Selected content type is invalid.");
      return;
    }

    let stepSettings: any = {};
    if (stepType === "object") {
      stepSettings.rotation = stepRotation;
    }

    const nextStepNumber =
      stepNumbers.length > 0 ? Math.max(...stepNumbers) + 1 : 1;

    const stepData: CreateStepData = {
      packageId: parseInt(id as string),
      label: stepNames,
      step: nextStepNumber,
      typeId: selectedContentType.id,
      settings: JSON.stringify(stepSettings),
    };

    const result = await dispatch(createStep(stepData));

    if (result.meta.requestStatus === "fulfilled") {
      const createdStepId = result.payload[0]?.id;

      if (thumbnail && createdStepId) {
        await dispatch(
          uploadContentThumbnail({ id: createdStepId, thumbnail })
        );
      }

      stepClickToPaths.forEach((clickToPath) => {
        const linkData = {
          packageContentId: createdStepId,
          targetPackageContentId: clickToPath.targetPackageContentId ?? 0,
          settings: clickToPath.angle.toString(),
        };

        dispatch(postLink(linkData)).then((linkResult) => {
          if (linkResult.meta.requestStatus === "fulfilled") {
          }
        });
      });

      if (stepFile && createdStepId) {
        await dispatch(
          uploadContent({
            id: createdStepId,
            primaryFile: stepFile,
            secondaryFile: secondaryFile || undefined,
          })
        );
      }

      navigate(`/steps-list/${id}`);
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: (file: File | null) => void
  ) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = (setFileFunction: (file: File | null) => void) => {
    setFileFunction(null);
  };

  const handleRemoveThumbnail = () => {
    setThumbnail(null);
    setThumbnailUrl(null);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.type === "dragenter" ||
      e.type === "dragleave" ||
      e.type === "dragover"
    ) {
      setDragActive(e.type === "dragenter" || e.type === "dragover");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setThumbnail(e.dataTransfer.files[0]);
      setThumbnailUrl(URL.createObjectURL(e.dataTransfer.files[0]));
    }
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        <form
          id="add-steps-form"
          className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-9xl flex flex-col relative"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center mb-2">
            <FaArrowLeft
              className="text-gray-600 cursor-pointer mr-2"
              onClick={() => navigate(`/steps-list/${id}`, { state })}
            />
            <h1 className="text-xl font-semibold">{t.addStep}</h1>
          </div>
          <div className="mb-2 relative">
            <label htmlFor="name" className="block mb-1 text-base ml-2">
              {t.name}
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:border-purple dark:bg-darkBackground"
                value={stepNames[language] || ""}
                onChange={(e) =>
                  setStepNames({ ...stepNames, [language]: e.target.value })
                }
                onClick={() => setIsStepNameModalOpen(true)}
                autoComplete="off"
              />
              <FaGlobe className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          <div className="mb-2 relative">
            <label htmlFor="type" className="block mb-1 text-base ml-2">
              {t.type}
            </label>
            <div className="relative">
              <select
                id="type"
                className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:border-purple dark:bg-darkBackground"
                value={stepType}
                onChange={(e) => setStepType(e.target.value)}
              >
                <option value="" disabled></option>
                {contentTypes.map((contentType) => (
                  <option key={contentType.id} value={contentType.name}>
                    {capitalizeFirstLetter(contentType.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor="file" className="block mb-1 text-base ml-2">
              {t.file}
            </label>
            <div className="relative">
              <input
                type="file"
                id="file"
                className="hidden"
                onChange={(e) => handleFileChange(e, setStepFile)}
              />
              <label
                htmlFor="file"
                className="w-full p-6 border border-dashed rounded-md shadow-sm cursor-pointer flex justify-center items-center relative dark:border-purple dark:bg-darkBackground"
              >
                {stepFile ? (
                  <div className="relative w-full">
                    <p className="text-center">{stepFile.name}</p>
                    <button
                      type="button"
                      className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(setStepFile);
                      }}
                    >
                      <FaTimes className="text-red-500" />
                    </button>
                  </div>
                ) : (
                  <p className="text-center">{t.uploadFile}</p>
                )}
              </label>
            </div>
          </div>

          {stepType === "object" && (
            <div className="mb-4">
              <label
                htmlFor="secondaryFile"
                className="block mb-1 text-base ml-2"
              >
                USDZ {t.file}{" "}
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="secondaryFile"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, setSecondaryFile)}
                />
                <label
                  htmlFor="secondaryFile"
                  className="w-full p-6 border border-dashed rounded-md shadow-sm cursor-pointer flex justify-center items-center relative dark:border-purple dark:bg-darkBackground"
                >
                  {secondaryFile ? (
                    <div className="relative w-full">
                      <p className="text-center">{secondaryFile.name}</p>
                      <button
                        type="button"
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(setSecondaryFile);
                        }}
                      >
                        <FaTimes className="text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-center">{t.uploadFile}</p>
                  )}
                </label>
                <span className="text-red-500 font-semibold text-sm">
                  {t2.animation_note}
                </span>
              </div>
            </div>
          )}

          {stepType === "object" && (
            <RotationInput
              rotation={stepRotation}
              setRotation={setStepRotation}
              label={t.rotation}
            />
          )}
          {stepType === "panorama" && (
            <PanoramaInputs
              clickToPaths={stepClickToPaths}
              setClickToPaths={setStepClickToPaths}
              links={links || []}
            />
          )}

          <div className="mb-4 relative">
            <label htmlFor="thumbnail" className="block mb-1 text-base ml-2">
              {t.thumbnail}
            </label>
            <div
              className={`w-full p-6 border border-dashed rounded-md shadow-sm dark:border-purple dark:bg-darkBackground ${
                dragActive ? "border-purple" : "border-gray-300"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="thumbnail"
                className="hidden"
                onChange={(e) => handleFileChange(e, setThumbnail)}
              />
              <label
                htmlFor="thumbnail"
                className="w-full h-full flex justify-center items-center cursor-pointer relative"
              >
                {thumbnailUrl ? (
                  <div className="relative">
                    <img
                      src={thumbnailUrl}
                      alt="Thumbnail"
                      className="max-h-40"
                    />
                  </div>
                ) : thumbnail ? (
                  <div className="relative">
                    <p className="text-center">{thumbnail.name}</p>
                  </div>
                ) : (
                  <p className="text-center">{t.thumbnailDrop}</p>
                )}
                {(thumbnailUrl || thumbnail) && (
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveThumbnail();
                    }}
                  >
                    <FaTimes className="text-red-500" />
                  </button>
                )}
              </label>
            </div>
          </div>

          <div className="absolute bottom-0 -mb-4 right-8 flex space-x-2">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded font-medium shadow-sm dark:bg-white dark:text-black"
              onClick={() => navigate(-1)}
            >
              {t.discard}
            </button>
            <button
              type="submit"
              className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
              form="add-steps-form"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
      <AddLanguageModal
        isOpen={isStepNameModalOpen}
        onClose={() => setIsStepNameModalOpen(false)}
        translations={t}
        mode="stepName"
        values={stepNames}
        setValues={setStepNames}
      />
    </div>
  );
};

export default CreateStep;
